import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { Button, Panel, SectionLabel } from '@/components/ui'
import { ADDRESSES } from '@/lib/contracts'
import feeDistAbi from '@/lib/abis/FeeDistributor.json'
import { fmtBalance, toNumber } from '@/lib/utils'
import { useAction } from '@/hooks/useAction'
import { useVaultsList } from '@/hooks/useVaultsList'
import { useFeeClaims } from '@/hooks/useFeeClaims'

/// Fees-follow-votes claims (FeeDistributor). A vault's 80% trading-fee remainder flows to the veNFT
/// holders who voted for its gauge that epoch. This is the "claim the fees you steered" surface,
/// distinct from the pooled-revenue panel (which pays the non-vault interest/liquidation legs). One
/// row per vault with anything owed; Claim banks every ended epoch for that vault in one call.
export function FeeClaimPanel({ onTx }) {
  const { address: user } = useAccount()
  const action = useAction()
  const fd = ADDRESSES.FEE_DISTRIBUTOR
  const { vaults } = useVaultsList()
  const { groups, hasAny, notDeployed, refetch } = useFeeClaims(user, vaults)

  if (notDeployed) return null

  async function claim(group) {
    const res = await action.run({
      calls: [{ address: fd, abi: feeDistAbi, functionName: 'claim', args: [group.vault.address, group.epochs] }],
      successMsg: `Claimed fees from ${group.vault.name}.`,
    })
    if (res) { await refetch(); onTx?.() }
  }

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Vault fees</SectionLabel>
      <p style={{ color: T.textDim, fontSize: 14, lineHeight: 1.6, margin: '10px 0 18px' }}>
        The trading fees from the gauges you voted for. Each vault keeps 80% of its fees for its voters;
        claim your share across every settled week here.
      </p>

      {!hasAny && (
        <div style={{ color: T.textDim, fontSize: 14 }}>
          {user ? 'No vault fees to claim yet. Vote for a vault gauge to earn its trading fees.' : 'Connect your wallet to see claimable vault fees.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {groups.map((g) => (
          <div key={g.vault.address} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: T.text, fontSize: 15 }}>{g.vault.name}</div>
              <div style={{ color: T.positive, fontSize: 13 }}>
                {g.total0 > 0n && <span>{fmtBalance(toNumber(g.total0, g.vault.token0.decimals), 4)} {g.vault.token0.symbol}</span>}
                {g.total0 > 0n && g.total1 > 0n && <span style={{ color: T.textDim }}> + </span>}
                {g.total1 > 0n && <span>{fmtBalance(toNumber(g.total1, g.vault.token1.decimals), 4)} {g.vault.token1.symbol}</span>}
              </div>
              <div style={{ color: T.textDim, fontSize: 11 }}>{g.epochs.length} week{g.epochs.length === 1 ? '' : 's'}</div>
            </div>
            <Button onClick={() => claim(g)} disabled={action.pending}>Claim</Button>
          </div>
        ))}
      </div>
    </Panel>
  )
}
